/**
 * Sprint 1069.E1-followup-3 — Cross-Studio "Glance" modal (⌘.).
 *
 * Read-only panoramic view of the highest-leverage move from EACH live
 * Mission Studio in a single dialog. Opens via Cmd+Period (⌘.) /
 * Ctrl+Period anywhere in the app — no matter what page you're on.
 *
 * Why this exists: the integrated-loop story breaks if a tenant has to
 * page through 4 studios to spot where today's friction is. With Glance,
 * the panoramic snapshot is one keystroke away.
 *
 * Shape: 4 rows (Sales · Marketing · Projects · Catalog), each carries:
 *   - The studio's accent + name + active mission
 *   - The alarm KPI (the 5th tile of every Studio dashboard — the one
 *     that says "we have a problem worth your attention")
 *   - A 1-line Next-Best-Action (compact rule pickers per studio)
 *   - A "Go →" button to enter the Studio
 *
 * Fetches all 4 dashboards in parallel. Re-fetches on each open (these
 * dashboards are < 200 ms each + cache-friendly).
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { X, ArrowRight, Sparkles, AlertTriangle, Target, Megaphone, Hammer, Boxes, Loader2, MapPin } from "lucide-react";
import { api } from "../../lib/api";
import { useLanguage } from "../../contexts/LanguageContext";
import { pathToMissionStage } from "./FlowBreadcrumb";

// Locked accents — single source of truth: /app/memory/STUDIO_SHELL_STANDARD.md § 3 Zone 2.
const STUDIOS = [
  {
    id: "sales",
    path: "/sales-studio",
    endpoint: "/sales-studio/dashboard",
    icon: Target,
    accent: "#3B82F6",
    label:   { en: "Sales",     es: "Ventas",     de: "Vertrieb"  },
    mission: { en: "Win the Client", es: "Ganar al Cliente", de: "Den Kunden gewinnen" },
    alarmKey: "stale_deals",
    alarmLabel: { en: "Stale Deals", es: "Tratos Estancados", de: "Tote Deals" },
    alarmCta: "/quotes?filter=stale",
  },
  {
    id: "marketing",
    path: "/marketing-studio",
    endpoint: "/marketing-studio/dashboard",
    icon: Megaphone,
    accent: "#EC4899",
    label:   { en: "Marketing", es: "Marketing",  de: "Marketing"   },
    mission: { en: "Grow the Brand", es: "Hacer Crecer la Marca", de: "Die Marke wachsen lassen" },
    alarmKey: "stale_drafts",
    alarmLabel: { en: "Stale Drafts", es: "Borradores Estancados", de: "Tote Entwürfe" },
    alarmCta: "/creative-studio?section=social&status=draft",
  },
  {
    id: "projects",
    path: "/projects-studio",
    endpoint: "/projects-studio/dashboard",
    icon: Hammer,
    accent: "#10B981",
    label:   { en: "Projects",  es: "Proyectos",  de: "Projekte"    },
    mission: { en: "Run the Project", es: "Ejecutar el Proyecto", de: "Das Projekt liefern" },
    alarmKey: "overdue_tasks",
    alarmLabel: { en: "Overdue Tasks", es: "Tareas Vencidas", de: "Überfällige Aufgaben" },
    alarmCta: "/my-tasks?filter=overdue",
  },
  {
    id: "catalog",
    path: "/catalog-studio",
    endpoint: "/catalog-studio/dashboard",
    icon: Boxes,
    accent: "#06B6D4",
    label:   { en: "Catalog",   es: "Catálogo",   de: "Katalog"     },
    mission: { en: "Catalog Health", es: "Salud del Catálogo", de: "Katalog-Gesundheit" },
    alarmKey: "low_stock",
    alarmLabel: { en: "Low Stock", es: "Bajo Stock", de: "Niedriger Bestand" },
    alarmCta: "/catalog/health",
  },
];

// Compact NBA pickers (mirror the rule snippets in each StudioPage).
function nbaSales(k, lang) {
  const stale = k?.stale_deals?.value ?? 0;
  const open = k?.open_leads?.value ?? 0;
  const wr = k?.win_rate?.value ?? 0;
  const wd = k?.win_rate?.denominator ?? 0;
  if (stale >= 5) return { tone: "warn", text: { en: `Review ${stale} stale deals first.`, es: `Revisa ${stale} tratos estancados primero.`, de: `Überprüfe zuerst ${stale} tote Deals.` }[lang] };
  if (wd >= 5 && wr < 25) return { tone: "warn", text: { en: `Win rate ${wr}% — refine the proposal.`, es: `Tasa de cierre ${wr}% — refina la propuesta.`, de: `Gewinnrate ${wr}% — verfeinere das Angebot.` }[lang] };
  if (open >= 10) return { tone: "info", text: { en: `${open} open leads — convert one into a brief.`, es: `${open} prospectos abiertos — convierte uno en brief.`, de: `${open} offene Leads — wandle einen in ein Briefing um.` }[lang] };
  if (stale >= 1) return { tone: "info", text: { en: `Nudge ${stale} stale deal${stale === 1 ? "" : "s"}.`, es: `Empuja ${stale} trato${stale === 1 ? "" : "s"} estancado${stale === 1 ? "" : "s"}.`, de: `Stoße ${stale} tote Deal${stale === 1 ? "" : "s"} an.` }[lang] };
  return { tone: "success", text: { en: "Pipeline is clean — pitch the next deal.", es: "Pipeline limpio — presenta el siguiente trato.", de: "Pipeline ist sauber — pitche den nächsten Deal." }[lang] };
}

function nbaMarketing(k, lang) {
  const stale = k?.stale_drafts?.value ?? 0;
  const bh = k?.brand_health?.value ?? 0;
  const content = k?.content_30d?.value ?? 0;
  if (bh < 50) return { tone: "warn", text: { en: `Brand health ${bh}/100 — finish brand setup.`, es: `Salud de marca ${bh}/100 — completa el setup.`, de: `Marken-Gesundheit ${bh}/100 — Setup abschließen.` }[lang] };
  if (stale >= 10) return { tone: "warn", text: { en: `${stale} drafts gathering dust — publish or archive.`, es: `${stale} borradores acumulando polvo — publica o archiva.`, de: `${stale} Entwürfe verstauben — veröffentliche oder archiviere.` }[lang] };
  if (content === 0) return { tone: "info", text: { en: "No content in 30 days — schedule one post.", es: "Sin contenido en 30 días — programa un post.", de: "Kein Content in 30 Tagen — plane einen Post." }[lang] };
  if (stale >= 1) return { tone: "info", text: { en: `${stale} stale draft${stale === 1 ? "" : "s"} waiting.`, es: `${stale} borrador${stale === 1 ? "" : "es"} esperando.`, de: `${stale} tote Entwurf${stale === 1 ? "" : "e"} warten.` }[lang] };
  return { tone: "success", text: { en: "Brand engine humming — keep posting.", es: "Motor de marca activo — sigue publicando.", de: "Markenmotor läuft — weiter posten." }[lang] };
}

function nbaProjects(k, lang) {
  const overdue = k?.overdue_tasks?.value ?? 0;
  const ot = k?.on_time_pct?.value ?? 0;
  const ad = k?.active_projects?.value ?? 0;
  if (overdue >= 10) return { tone: "warn", text: { en: `${overdue} tasks overdue — triage them first.`, es: `${overdue} tareas vencidas — triáguelas primero.`, de: `${overdue} überfällige Aufgaben — zuerst triagieren.` }[lang] };
  if (ad >= 1 && ot < 70) return { tone: "warn", text: { en: `On-time ${ot}% — rescue the slipping project.`, es: `Puntualidad ${ot}% — rescata el proyecto atrasado.`, de: `Pünktlichkeit ${ot}% — rette das verzögerte Projekt.` }[lang] };
  if (overdue >= 1) return { tone: "info", text: { en: `${overdue} task${overdue === 1 ? "" : "s"} overdue — handle them.`, es: `${overdue} tarea${overdue === 1 ? "" : "s"} vencida${overdue === 1 ? "" : "s"} — atiéndelas.`, de: `${overdue} überfällige Aufgabe${overdue === 1 ? "" : "n"} — anpacken.` }[lang] };
  return { tone: "success", text: { en: "Projects are on schedule.", es: "Proyectos al día.", de: "Projekte im Plan." }[lang] };
}

function nbaCatalog(k, lang) {
  const low = k?.low_stock?.value ?? 0;
  const used = k?.items_used_30d?.value ?? 0;
  if (low >= 5) return { tone: "warn", text: { en: `${low} items low on stock — restock now.`, es: `${low} artículos con bajo stock — repón ahora.`, de: `${low} Artikel mit niedrigem Bestand — jetzt nachfüllen.` }[lang] };
  if (low >= 1) return { tone: "info", text: { en: `${low} item${low === 1 ? "" : "s"} running low.`, es: `${low} artículo${low === 1 ? "" : "s"} bajo${low === 1 ? "" : "s"}.`, de: `${low} Artikel knapp.` }[lang] };
  if (used === 0) return { tone: "info", text: { en: "Catalog idle — bundle hot items.", es: "Catálogo inactivo — agrupa los más usados.", de: "Katalog ruht — beliebte Artikel bündeln." }[lang] };
  return { tone: "success", text: { en: `Catalog active — ${used} items quoted recently.`, es: `Catálogo activo — ${used} artículos cotizados recientemente.`, de: `Katalog aktiv — ${used} Artikel kürzlich angeboten.` }[lang] };
}

const NBA_PICKERS = { sales: nbaSales, marketing: nbaMarketing, projects: nbaProjects, catalog: nbaCatalog };

const TONE_BG = { warn: "#F59E0B", info: "#3B82F6", success: "#10B981" };

export default function GlanceModal({ open, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, t } = useLanguage();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  // Sprint 1070.C5 — "📍 You are here" lifeline. Resolves the current page's
  // mission + stage so the user immediately sees their location in the
  // journey when they pop Glance. Mirrors the global FlowBreadcrumb
  // semantics — single source of truth via `pathToMissionStage`.
  const youAreHere = useMemo(() => {
    const detected = pathToMissionStage(location.pathname);
    if (!detected) return null;
    const MISSION_LABEL = {
      "win-the-client":  { en: "Win the Client",  es: "Ganar al Cliente",      de: "Den Kunden gewinnen" },
      "grow-the-brand":  { en: "Grow the Brand",  es: "Hacer Crecer la Marca", de: "Die Marke wachsen lassen" },
      "run-the-project": { en: "Run the Project", es: "Ejecutar el Proyecto",  de: "Das Projekt liefern" },
      "get-paid":        { en: "Get Paid",        es: "Cobrar",                de: "Bezahlt werden" },
    };
    const STAGE_LABEL = {
      lead: "Lead", concept: "Concept", quote: "Quote", proposal: "Proposal", pitch: "Pitch", win: "Win",
      diagnose: "Diagnose", position: "Position", build: "Build", plan: "Plan",
      create: "Create", publish: "Publish", measure: "Measure", improve: "Improve",
      production: "Production", deliver: "Deliver",
      invoice: "Invoice", paid: "Paid",
    };
    const STUDIO_ACCENT = {
      "win-the-client":  "#3B82F6",
      "grow-the-brand":  "#EC4899",
      "run-the-project": "#10B981",
      "get-paid":        "#F59E0B",
    };
    return {
      mission: MISSION_LABEL[detected.mission]?.[language] || MISSION_LABEL[detected.mission]?.en || detected.mission,
      stage:   STAGE_LABEL[detected.stage] || detected.stage,
      accent:  STUDIO_ACCENT[detected.mission] || "#3B82F6",
      missionId: detected.mission,
      stageId:   detected.stage,
    };
  }, [location.pathname, language]);

  // Fetch all 4 dashboards in parallel each time the modal opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    Promise.all(STUDIOS.map(s =>
      api.get(s.endpoint)
        .then(r => ({ studio: s, kpis: r.data?.kpis || {}, error: false }))
        .catch(() => ({ studio: s, kpis: {}, error: true }))
    )).then(results => {
      if (cancelled) return;
      setRows(results);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [open]);

  const goStudio = useCallback((s) => {
    try { sessionStorage.setItem("sp:sidebar-pulse-path", s.path); } catch { /* noop */ }
    onClose?.();
    navigate(s.path);
  }, [navigate, onClose]);

  const goAlarm = useCallback((s) => {
    onClose?.();
    navigate(s.alarmCta);
  }, [navigate, onClose]);

  // Close on Esc.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-start justify-center pt-20 px-4"
      onClick={onClose}
      data-testid="glance-modal-overlay"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-2xl rounded-2xl border border-[var(--app-border)] bg-[var(--app-bg)] shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="glance-modal"
      >
        {/* Header */}
        <div className="relative px-6 py-5 border-b border-[var(--app-border)] bg-gradient-to-br from-pink-500/10 via-blue-500/5 to-transparent">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-pink-400 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> {t("Cross-Studio Glance")}
              </p>
              <h2 className="text-xl font-bold text-[var(--app-text)] mt-1" data-testid="glance-title">
                {t("Your highest-leverage move, everywhere.")}
              </h2>
              <p className="text-xs text-[var(--app-text-muted)] mt-0.5">
                {t("Press")} <kbd className="px-1.5 py-0.5 text-[10px] font-mono rounded bg-[var(--app-surface)] border border-[var(--app-border)]">⌘ .</kbd> {t("anywhere to reopen.")}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-[var(--app-surface)] text-[var(--app-text-muted)]"
              aria-label={t("Close")}
              data-testid="glance-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {/* Sprint 1070.C5 — "📍 You are here" lifeline. Renders ONLY when
              the current page resolves to a mission/stage. Cuts the cognitive
              cost of "wait, where am I in the journey?" the moment Glance pops. */}
          {youAreHere && (
            <div
              className="mt-3 flex items-center gap-2.5 px-3 py-2 rounded-lg border"
              style={{ borderColor: `${youAreHere.accent}55`, backgroundColor: `${youAreHere.accent}12` }}
              data-testid="glance-you-are-here"
              data-mission={youAreHere.missionId}
              data-stage={youAreHere.stageId}
            >
              <MapPin className="w-3.5 h-3.5 flex-shrink-0" style={{ color: youAreHere.accent }} />
              <p className="text-[11px] text-[var(--app-text)] leading-tight">
                <span className="uppercase tracking-wider text-[9px] font-bold opacity-60 mr-1.5">
                  {t("You are here")}
                </span>
                <span className="font-semibold">{youAreHere.mission}</span>
                <span className="opacity-60 mx-1.5">·</span>
                <span style={{ color: youAreHere.accent }} className="font-semibold">{youAreHere.stage}</span>
              </p>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="p-4 max-h-[60vh] overflow-y-auto">
          {loading || !rows ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-5 h-5 animate-spin text-[var(--app-text-muted)]" />
            </div>
          ) : (
            <div className="space-y-2.5">
              {rows.map(({ studio, kpis, error }) => {
                const Icon = studio.icon;
                const alarm = kpis?.[studio.alarmKey];
                const alarmValue = alarm?.value ?? 0;
                const nba = NBA_PICKERS[studio.id](kpis, language) || { tone: "info", text: "—" };
                const studioLabel = studio.label?.[language] || studio.label.en;
                const missionLabel = studio.mission?.[language] || studio.mission.en;
                const alarmLabelText = studio.alarmLabel?.[language] || studio.alarmLabel.en;
                return (
                  <div
                    key={studio.id}
                    className="group relative rounded-xl border bg-[var(--app-surface)] overflow-hidden hover:border-[color:var(--app-text-muted)] transition-colors"
                    style={{ borderColor: `${studio.accent}30` }}
                    data-testid={`glance-row-${studio.id}`}
                  >
                    {/* accent bar */}
                    <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: studio.accent }} />
                    <div className="pl-4 pr-3 py-3 flex items-center gap-3">
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${studio.accent}1F`, color: studio.accent }}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-bold text-[var(--app-text)]" data-testid={`glance-studio-name-${studio.id}`}>
                            {studioLabel}
                          </span>
                          <span className="text-[10px] uppercase tracking-widest font-semibold opacity-60" style={{ color: studio.accent }}>
                            {missionLabel}
                          </span>
                          {error && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/20 uppercase font-bold">
                              {t("offline")}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-[var(--app-text)] mt-1 leading-snug" data-testid={`glance-nba-${studio.id}`}>
                          <span
                            className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle"
                            style={{ backgroundColor: TONE_BG[nba.tone] }}
                          />
                          {nba.text}
                        </p>
                      </div>
                      {/* Alarm KPI tile (compact) */}
                      <button
                        onClick={() => goAlarm(studio)}
                        disabled={alarmValue === 0 || error}
                        className="shrink-0 text-right rounded-lg border border-[var(--app-border)] bg-[var(--app-bg)] px-2.5 py-1.5 disabled:opacity-50 hover:border-amber-500/40 transition-colors"
                        data-testid={`glance-alarm-${studio.id}`}
                        title={alarmValue > 0 ? `${alarmLabelText} → ${studio.alarmCta}` : alarmLabelText}
                      >
                        <div className="flex items-center justify-end gap-1">
                          {alarmValue > 0 && <AlertTriangle className="w-3 h-3 text-amber-500" />}
                          <span className="text-base font-bold leading-none" style={{ color: alarmValue > 0 ? "#F59E0B" : "var(--app-text-muted)" }}>
                            {alarmValue}
                          </span>
                        </div>
                        <p className="text-[9px] uppercase tracking-widest text-[var(--app-text-muted)] mt-0.5 truncate max-w-[80px]">
                          {alarmLabelText}
                        </p>
                      </button>
                      <button
                        onClick={() => goStudio(studio)}
                        className="shrink-0 inline-flex items-center gap-1 rounded-lg px-2.5 py-2 text-[11px] font-bold text-white hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: studio.accent }}
                        data-testid={`glance-go-${studio.id}`}
                      >
                        {t("Go")} <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footnote */}
        <div className="px-6 py-3 border-t border-[var(--app-border)] text-[10px] text-[var(--app-text-muted)] flex items-center justify-between">
          <span className="flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-pink-400" />
            {t("Read-only — opens in any Studio with ⌘ .")}
          </span>
          <span>
            {t("Esc to close")}
          </span>
        </div>
      </div>
    </div>,
    document.body
  );
}
